import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import AdminShell, { Panel, Btn } from "@/components/admin/AdminShell";
import { api, getErrorMessage } from "@/lib/api";

const MODULES = [
  { id: "spot", label: "Spot Trading" },
  { id: "otc", label: "OTC Trading" },
];

const DEDUCT_OPTIONS = [
  { value: "seller", label: "Seller" },
  { value: "buyer", label: "Buyer" },
  { value: "both", label: "Both (50/50)" },
];

export default function TdsSettings() {
  const [settings, setSettings] = useState([]);
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api
      .get("/api/admin/tds/settings")
      .then(({ data }) => setSettings(data.settings || []))
      .catch((err) => setMsg(getErrorMessage(err) || "Failed to load TDS settings"));
  }, []);

  function updateModule(module, patch) {
    setSettings((prev) =>
      prev.map((s) => (s.module === module ? { ...s, ...patch } : s)),
    );
  }

  async function saveModule(module) {
    const row = settings.find((s) => s.module === module);
    if (!row) return;
    setSaving(module);
    setMsg("");
    const { data } = await api.patch(`/api/admin/tds/settings/${module}`, {
      enabled: row.enabled,
      tds_percentage: Number(row.tds_percentage),
      minimum_amount: Number(row.minimum_amount),
      currency: row.currency || "INR",
      deduct_from: row.deduct_from,
    });
    setSettings((prev) => prev.map((s) => (s.module === module ? data : s)));
    setMsg(`${module.toUpperCase()} saved`);
    setSaving(null);
    setTimeout(() => setMsg(""), 2000);
  }

  return (
    <AdminShell title="Finance — TDS Settings" subtitle="Configure module-wise TDS rates and thresholds">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {MODULES.map(({ id, label }) => {
          const row = settings.find((s) => s.module === id) || {};
          return (
            <Panel key={id} className="p-5" data-testid={`tds-settings-${id}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[14px] font-bold">{label}</h3>
                <label className="flex items-center gap-2 text-[12px]">
                  <input
                    type="checkbox"
                    checked={!!row.enabled}
                    onChange={(e) => updateModule(id, { enabled: e.target.checked })}
                    data-testid={`tds-enabled-${id}`}
                  />
                  Enabled
                </label>
              </div>
              <div className="space-y-3 text-[13px]">
                <label className="block">
                  <span className="text-[#8892A8] text-[12px]">TDS %</span>
                  <input
                    type="number"
                    step="0.01"
                    className="mt-1 w-full h-[36px] px-3 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px]"
                    value={row.tds_percentage ?? 1}
                    onChange={(e) => updateModule(id, { tds_percentage: e.target.value })}
                    data-testid={`tds-pct-${id}`}
                  />
                </label>
                <label className="block">
                  <span className="text-[#8892A8] text-[12px]">Minimum amount (INR)</span>
                  <input
                    type="number"
                    className="mt-1 w-full h-[36px] px-3 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px]"
                    value={row.minimum_amount ?? 10000}
                    onChange={(e) => updateModule(id, { minimum_amount: e.target.value })}
                    data-testid={`tds-min-${id}`}
                  />
                </label>
                <label className="block">
                  <span className="text-[#8892A8] text-[12px]">Deduct from</span>
                  <select
                    className="mt-1 w-full h-[36px] px-3 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px]"
                    value={row.deduct_from || "seller"}
                    onChange={(e) => updateModule(id, { deduct_from: e.target.value })}
                    data-testid={`tds-deduct-${id}`}
                  >
                    {DEDUCT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <Btn
                className="mt-4 w-full justify-center"
                onClick={() => saveModule(id)}
                disabled={saving === id}
                data-testid={`tds-save-${id}`}
              >
                <Save size={14} /> {saving === id ? "Saving…" : "Save"}
              </Btn>
            </Panel>
          );
        })}
      </div>
      {msg && <p className="mt-4 text-[13px] text-[#10B981]">{msg}</p>}
    </AdminShell>
  );
}
